import { getRandomInt } from "../Shared/Helpers.tsx";
import { generateProblemPages } from "../Shared/Generation/problemPages.ts";
import {
    AreaFigureSpec,
    isAreaFigureQuestion,
    ProblemData,
    ScaleDrawingSpec,
    ShapeQuestionOptions,
    SUPERSCRIPT,
} from "./Constants.ts";
import { areaFigureGeometry } from "../Shared/Pdf/areaFigureGeometry.ts";
import { ISettingsProps } from "./Models.ts";
import { PrintedLanguage, printedLanguageOf } from "./PrintedLanguage.ts";
import { shapePrompts as en } from "../Files/Prompts/en/shapes.ts";
import { shapePrompts as es } from "../Files/Prompts/es/shapes.ts";
import { shapePrompts as fr } from "../Files/Prompts/fr/shapes.ts";

const PROMPTS: Record<PrintedLanguage, typeof en> = { en, es, fr };

/**
 * Perimeter and area of rectangles. See ADR 0038.
 *
 * The rule that shapes this file is ADR 0038 §2, and it is a constraint on
 * *content* imposed to protect a *drawing* - the first of its kind in this
 * repo:
 *
 *   The two sides of a rectangle differ by at most a factor of four.
 *
 * A rectangle labelled 40 cm by 3 cm cannot be drawn at 40:3 on a worksheet,
 * so it would be drawn at some other ratio, and the picture would then
 * contradict its own numbers. Printing "not to scale" is a disclaimer rather
 * than a fix - the same move ADR 0022 refuses over printed rulers. So the
 * question is never generated, and every picture on the sheet is true.
 *
 * That is invisible in the output: nothing on a sheet says the sides were
 * drawn within a ratio. `ShapeGenerator.test.ts` is what says so.
 */

/** ADR 0038 §2. Beyond this a rectangle cannot be drawn at its labelled ratio. */
export const MAX_ASPECT_RATIO = 4;

/** Below this a "rectangle" is a line at any drawable size. */
const SMALLEST_SIDE = 2;

const AREA_UNIT = `cm${SUPERSCRIPT[2]}`;
const LENGTH_UNIT = "cm";

/**
 * Scale Drawing (ADR 0107). The drawing's lengths are in cm and the actual
 * ones in metres, so the units are fixed and a scale is just metres per cm.
 */
const ACTUAL_UNIT = "m";
const ACTUAL_AREA_UNIT = `m${SUPERSCRIPT[2]}`;
const UNKNOWN = "?";

/** ADR 0107 §4: 1 cm = this many metres. */
export const SCALES = [2, 3, 4, 5, 10];

/** ADR 0107 §1: a drawing longer than a page is no drawing. */
export const SCALE_DRAWING_MAX_SIDE = 12;

/**
 * The four questions a Scale Drawing sheet deals (ADR 0107 §3), evenly on each
 * printed page.
 */
export type ScaleDrawingKind = "actualSize" | "actualArea" | "drawingLength" | "anotherScale";

export const SCALE_DRAWING_KINDS: ScaleDrawingKind[] = [
    "actualSize",
    "actualArea",
    "drawingLength",
    "anotherScale",
];

/**
 * Where an Another scale pair `(drawing scale, new scale)` is built from when
 * no scale in the list fits the rectangle drawn. Each drawing scale here is a
 * whole multiple of its new scale, so every whole-cm side divides exactly, and
 * the new sides (twice the drawing's) stay on the page.
 */
const FALLBACK_SCALE_PAIRS: [number, number][] = [
    [4, 2],
    [10, 5],
];

/** The three figures of ADR 0121, and the one question type each is. */
const AREA_FIGURE_QUESTIONS = [
    ShapeQuestionOptions.Parallelogram,
    ShapeQuestionOptions.Trapezoid,
    ShapeQuestionOptions.Triangle,
];

function questionTypes(question: ShapeQuestionOptions): ShapeQuestionOptions[] {
    if (question === ShapeQuestionOptions.Polygons) {
        return AREA_FIGURE_QUESTIONS;
    }
    if (question === ShapeQuestionOptions.Mixed) {
        return [
            ShapeQuestionOptions.Perimeter,
            ShapeQuestionOptions.Area,
            ShapeQuestionOptions.MissingSide,
        ];
    }
    return [question];
}

function usableMax(maxSide: number): number {
    return Math.max(4, Math.floor(maxSide) || 20);
}

/**
 * Two sides within the drawable ratio.
 *
 * The shorter is drawn first and the longer from within four times it, so the
 * bound holds by construction rather than by rejection - there is no draw to
 * throw away and no failure path.
 *
 * `distinct` keeps the longer strictly longer (a Scale Drawing is never a
 * square, ADR 0107 §4): the shorter stops one short of the cap and the longer
 * starts one above it, which is still inside the ratio and still a draw, not a
 * rejection. With `distinct` off the draws are exactly what they were before,
 * so every recorded sheet is unchanged.
 */
function sides(max: number, distinct = false): { width: number; height: number } {
    const shorter = getRandomInt(SMALLEST_SIDE, Math.max(SMALLEST_SIDE, distinct ? max - 1 : max));
    const longest = Math.min(max, shorter * MAX_ASPECT_RATIO);
    const longer = getRandomInt(distinct ? shorter + 1 : shorter, Math.max(shorter, longest));
    // Which of the two is the width is its own coin flip, so a sheet is not
    // all landscape rectangles.
    return getRandomInt(0, 1) === 0
        ? { width: longer, height: shorter }
        : { width: shorter, height: longer };
}

const scaleText = (scale: number) => `1 ${LENGTH_UNIT} = ${scale} ${ACTUAL_UNIT}`;

/**
 * The same scale for a sentence, with no-break spaces (WinAnsi 0xA0, so the PDF
 * font has it). A prompt wraps at 20pt, and "1 cm" over "= 2 m" on the next
 * line reads as two things.
 */
const NO_BREAK_SPACE = String.fromCharCode(0xa0);
const unbroken = (scale: number) => scaleText(scale).split(" ").join(NO_BREAK_SPACE);

/**
 * One Scale Drawing problem (ADR 0107). Always succeeds, and is built from the
 * answer: the drawing's sides and the scale are drawn first, and every number a
 * student must produce is a product or exact quotient of them.
 *
 * The drawing is always landscape, so "length" is the side along the bottom and
 * "width" the side beside it, and an answer read as "length by width" has one
 * order. The sides are whole cm from 2 to the smaller of the dial and 12, never
 * a square, inside the 4:1 limit of `sides`.
 *
 * Another scale needs a second scale that divides both actual sides. Of the
 * scales in the list other than the drawing's, those that divide both and keep
 * the new sides between 2 cm and twice the cap are the candidates, and one is
 * picked. If none is, the pair of scales is chosen from `FALLBACK_SCALE_PAIRS`
 * (the new scale is fixed first and the drawing's scale made a multiple of it),
 * so it never rejects and never loops.
 */
function buildScaleDrawingProblem(
    kind: ScaleDrawingKind,
    maxSide: number,
    key: number,
    language: PrintedLanguage
): ProblemData {
    const t = PROMPTS[language];
    const cap = Math.min(usableMax(maxSide), SCALE_DRAWING_MAX_SIDE);
    let scale = SCALES[getRandomInt(0, SCALES.length - 1)];
    const drawn = sides(cap, true);
    const width = Math.max(drawn.width, drawn.height);
    const height = Math.min(drawn.width, drawn.height);
    let newScale = 0;

    if (kind === "anotherScale") {
        const fits = (candidate: number) =>
            candidate !== scale &&
            (width * scale) % candidate === 0 &&
            (height * scale) % candidate === 0 &&
            (height * scale) / candidate >= SMALLEST_SIDE &&
            (width * scale) / candidate <= 2 * cap;
        const candidates = SCALES.filter(fits);
        if (candidates.length > 0) {
            newScale = candidates[getRandomInt(0, candidates.length - 1)];
        } else {
            [scale, newScale] = FALLBACK_SCALE_PAIRS[getRandomInt(0, FALLBACK_SCALE_PAIRS.length - 1)];
        }
    }

    const actualWidth = width * scale;
    const actualHeight = height * scale;
    const spec: ScaleDrawingSpec = { scale, width, height, line: t.scaleLine(scaleText(scale)) };
    let widthLabel: string = `${width} ${LENGTH_UNIT}`;
    let heightLabel: string = `${height} ${LENGTH_UNIT}`;
    let display: string[];

    if (kind === "actualSize") {
        display = [
            t.actualSize(),
            t.bySize(`${actualWidth} ${ACTUAL_UNIT}`, `${actualHeight} ${ACTUAL_UNIT}`),
        ];
    } else if (kind === "actualArea") {
        display = [t.actualArea(), `${actualWidth * actualHeight} ${ACTUAL_AREA_UNIT}`];
    } else if (kind === "drawingLength") {
        const hideWidth = getRandomInt(0, 1) === 0;
        spec.hidden = hideWidth ? "width" : "height";
        if (hideWidth) {
            widthLabel = UNKNOWN;
        } else {
            heightLabel = UNKNOWN;
        }
        display = [
            t.drawingLength(`${hideWidth ? actualWidth : actualHeight} ${ACTUAL_UNIT}`),
            `${hideWidth ? width : height} ${LENGTH_UNIT}`,
        ];
    } else {
        spec.newScale = newScale;
        display = [
            t.anotherScale(unbroken(newScale)),
            t.bySize(
                `${actualWidth / newScale} ${LENGTH_UNIT}`,
                `${actualHeight / newScale} ${LENGTH_UNIT}`
            ),
        ];
    }

    return {
        key,
        numbers: [],
        operator: "",
        display,
        rectangle: { width, height, widthLabel, heightLabel },
        scaleDrawing: spec,
        shapeQuestion: ShapeQuestionOptions.ScaleDrawing,
    };
}

/**
 * Parallelogram, trapezoid and triangle area (ADR 0121).
 *
 * Three rules shape the draw, and none is visible on the sheet:
 *
 * - **Every area is whole** (§3). The height is drawn after the base, from the
 *   heights that make `base * height` (triangle) or `(base + top) * height`
 *   (trapezoid) even, so the parity holds by construction. A parallelogram's is
 *   always whole.
 * - **The figure can be drawn to scale** (0038 §2). Its overall width, the
 *   furthest right corner, and its height are within 4:1: the offset is drawn
 *   before the height, which is then drawn from the range that keeps the ratio.
 * - **The height label fits** (§4). `areaFigureGeometry` says whether it clears
 *   the dashed line, every side and the right-angle mark, at the half-page size
 *   (the smaller box; the full page's is bigger and holds whatever fits here).
 *   The qualifying set is too big to list at the 100 dial (the trapezoid's runs
 *   to a couple of million figures), so a figure is drawn by the rules above and
 *   kept if it qualifies, the draw repeated otherwise. At the 100 dial a draw
 *   qualifies about 30% of the time for a parallelogram, 20% for a trapezoid and
 *   6% for a triangle (its apex splits the room beside the height between two
 *   small gaps, and its foot must stand clear of both ends), so 500 attempts fail
 *   about once in 10^12 triangles; the figures in
 *   `FALLBACK_FIGURES` are there so that even then nothing unreadable prints.
 *
 * Every labelled length is from 2 to the dial. The offset is whole and never
 * printed: 1 to `base - 1` for a parallelogram or triangle; for a trapezoid the
 * top is 2 to `base - 2` and the offset 1 to `base - 1 - top`, so both legs slant.
 */

/** The sizes a figure is drawn at; the half page's is the smaller, and is what qualifies a figure. */
export const AREA_FIGURE_SIZE = {
    full: { maxWidth: 150, maxHeight: 70 },
    half: { maxWidth: 120, maxHeight: 56 },
};

/** The smallest dial: the figures need room for a height label beside the dashed line. */
const SMALLEST_FIGURE_DIAL = 12;

/** How many times a draw is repeated for a figure whose height label does not fit. */
const FIGURE_ATTEMPTS = 500;

/** A figure that qualifies at any dial, for the draw that runs out of attempts. */
export const FALLBACK_FIGURES: Record<AreaFigureSpec["kind"], AreaFigureSpec> = {
    parallelogram: { kind: "parallelogram", base: 8, height: 6, offset: 3 },
    trapezoid: { kind: "trapezoid", base: 10, top: 5, height: 6, offset: 2 },
    triangle: { kind: "triangle", base: 10, height: 4, offset: 3 },
};

const FIGURE_KIND_OF: Record<string, AreaFigureSpec["kind"]> = {
    [ShapeQuestionOptions.Parallelogram]: "parallelogram",
    [ShapeQuestionOptions.Trapezoid]: "trapezoid",
    [ShapeQuestionOptions.Triangle]: "triangle",
};

/** A whole number from `low` to `high`, or `low` if the range is empty. */
const between = (low: number, high: number) => getRandomInt(low, Math.max(low, high));

/** A height from `low` to `high` whose product with `factor` is even. */
function evenProductHeight(factor: number, low: number, high: number): number {
    if (factor % 2 === 0) {
        return between(low, high);
    }
    const first = low + (low % 2);
    return first + 2 * getRandomInt(0, Math.max(0, Math.floor((high - first) / 2)));
}

/** The labels a figure prints. */
export function figureLabels(figure: AreaFigureSpec) {
    return {
        base: `${figure.base} ${LENGTH_UNIT}`,
        height: `${figure.height} ${LENGTH_UNIT}`,
        top: figure.top === undefined ? undefined : `${figure.top} ${LENGTH_UNIT}`,
    };
}

/** Does the figure's height label fit at the half-page size? (ADR 0121 §4) */
export function areaFigureQualifies(figure: AreaFigureSpec, size = AREA_FIGURE_SIZE.half): boolean {
    return areaFigureGeometry({ ...figure, labels: figureLabels(figure), ...size }).fits;
}

/** One figure by the rules above, before the fit is asked. */
export function drawAreaFigure(kind: AreaFigureSpec["kind"], dial: number): AreaFigureSpec {
    const base = getRandomInt(kind === "trapezoid" ? 4 : SMALLEST_SIDE, dial);
    let top: number | undefined;
    let offset: number;
    let width: number;
    if (kind === "parallelogram") {
        offset = getRandomInt(1, base - 1);
        width = base + offset;
    } else if (kind === "trapezoid") {
        top = getRandomInt(SMALLEST_SIDE, base - 2);
        offset = getRandomInt(1, base - 1 - top);
        width = base;
    } else {
        offset = getRandomInt(1, base - 1);
        width = base;
    }
    const low = Math.max(SMALLEST_SIDE, Math.ceil(width / MAX_ASPECT_RATIO));
    const high = Math.min(dial, width * MAX_ASPECT_RATIO);
    const height =
        kind === "parallelogram"
            ? between(low, high)
            : evenProductHeight(kind === "trapezoid" ? base + (top as number) : base, low, high);
    return top === undefined ? { kind, base, height, offset } : { kind, base, top, height, offset };
}

function buildAreaFigureProblem(
    question: ShapeQuestionOptions,
    maxSide: number,
    key: number,
    language: PrintedLanguage
): ProblemData {
    const dial = Math.max(SMALLEST_FIGURE_DIAL, Math.floor(maxSide) || 20);
    // Polygon Area is dealt by `generateShapePages`; asked for directly it picks a figure itself.
    const asked = AREA_FIGURE_QUESTIONS.includes(question)
        ? question
        : AREA_FIGURE_QUESTIONS[getRandomInt(0, AREA_FIGURE_QUESTIONS.length - 1)];
    const kind = FIGURE_KIND_OF[asked];
    let figure = FALLBACK_FIGURES[kind];
    for (let attempt = 0; attempt < FIGURE_ATTEMPTS; attempt++) {
        const drawn = drawAreaFigure(kind, dial);
        if (areaFigureQualifies(drawn)) {
            figure = drawn;
            break;
        }
    }
    // Whole by construction: what is halved is even.
    const area =
        kind === "parallelogram"
            ? figure.base * figure.height
            : kind === "trapezoid"
            ? ((figure.base + (figure.top as number)) * figure.height) / 2
            : (figure.base * figure.height) / 2;
    return {
        key,
        numbers: [],
        operator: "",
        display: [PROMPTS[language].area(), `${area} ${AREA_UNIT}`],
        areaFigure: figure,
        shapeQuestion: asked,
    };
}

/**
 * One shape problem. Always succeeds.
 *
 * The rectangle rides on its own `ProblemData` field. `display` carries the
 * prompt and the answer, and the answer includes its unit because the unit is
 * the part this topic exists to teach (ADR 0038 §3).
 */
export function buildShapeProblem(
    question: ShapeQuestionOptions,
    maxSide: number,
    key: number,
    language: PrintedLanguage = 'en',
    scaleKind: ScaleDrawingKind = 'actualSize'
): ProblemData {
    if (question === ShapeQuestionOptions.ScaleDrawing) {
        return buildScaleDrawingProblem(scaleKind, maxSide, key, language);
    }
    if (isAreaFigureQuestion(question)) {
        return buildAreaFigureProblem(question, maxSide, key, language);
    }

    const max = usableMax(maxSide);
    const { width, height } = sides(max);
    const area = width * height;
    const t = PROMPTS[language];

    if (question === ShapeQuestionOptions.MissingSide) {
        // Answer-first: both sides are drawn, the area computed, then one side
        // hidden - so the division a student does is exact by construction.
        const hideWidth = getRandomInt(0, 1) === 0;
        return {
            key,
            numbers: [],
            operator: "",
            display: [
                t.missingSide(`${area} ${AREA_UNIT}`),
                `${hideWidth ? width : height} ${LENGTH_UNIT}`,
            ],
            rectangle: {
                width,
                height,
                widthLabel: hideWidth ? undefined : `${width} ${LENGTH_UNIT}`,
                heightLabel: hideWidth ? `${height} ${LENGTH_UNIT}` : undefined,
            },
            shapeQuestion: question,
        };
    }

    const perimeter = 2 * (width + height);
    const asking = question === ShapeQuestionOptions.Perimeter;

    return {
        key,
        numbers: [],
        operator: "",
        display: [
            asking ? t.perimeter() : t.area(),
            asking ? `${perimeter} ${LENGTH_UNIT}` : `${area} ${AREA_UNIT}`,
        ],
        rectangle: {
            width,
            height,
            widthLabel: `${width} ${LENGTH_UNIT}`,
            heightLabel: `${height} ${LENGTH_UNIT}`,
        },
        shapeQuestion: question,
    };
}

// Two rectangles of the same size asking the same thing are the same problem
// however they were drawn. On a Scale Drawing the scales belong to the problem:
// 6 by 4 at 1 cm = 3 m and at 1 cm = 5 m have different answers.
function fingerprint(problem: ProblemData): string {
    const figure = problem.areaFigure;
    if (figure) {
        // The offset is not printed, so figures that differ only in it are the same problem.
        return `${problem.shapeQuestion}:${figure.base}x${figure.top ?? 0}x${figure.height}`;
    }
    const shape = problem.rectangle!;
    const drawing = problem.scaleDrawing;
    const scales = drawing ? `:${drawing.scale}>${drawing.newScale ?? ""}:${drawing.hidden ?? ""}` : "";
    return `${problem.shapeQuestion}:${shape.width}x${shape.height}${scales}`;
}

/** A page-shaped set of shape problems. */
export function generateShapePages(settings: ISettingsProps): ProblemData[][] | null {
    const question = settings.ShapeQuestion ?? ShapeQuestionOptions.Mixed;
    const maxSide = settings.ShapeMaxSide ?? 20;
    const language = printedLanguageOf(settings);
    if (question === ShapeQuestionOptions.ScaleDrawing) {
        // Dealt page by page, not across the sheet: each printed page, and each
        // half of a half page (one call here per half), goes to a student and
        // must hold the four questions as evenly as it can (ADR 0107 §3).
        const perPage = settings.NumberOfProblemsPerPage;
        const pages: ProblemData[][] = [];
        for (let page = 0; page < settings.NumberOfPages; page++) {
            const onePage = generateProblemPages({
                settings: { ...settings, NumberOfPages: 1 },
                choices: SCALE_DRAWING_KINDS,
                build: (kind, key) => buildShapeProblem(question, maxSide, key, language, kind),
                fingerprint,
            });
            if (onePage === null) {
                return pages.length === 0 ? null : pages;
            }
            // Keys number the whole sheet, not the page they were drawn on.
            pages.push(onePage[0].map((problem) => ({ ...problem, key: page * perPage + problem.key })));
        }
        return pages;
    }
    return generateProblemPages({
        settings,
        choices: questionTypes(question),
        build: (type, key) => buildShapeProblem(type, maxSide, key, language),
        fingerprint,
        // Each printed page goes to a student: all three figures on it (ADR 0121 §1).
        dealPerPage: question === ShapeQuestionOptions.Polygons,
    });
}
